"use client";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Layout, Typography, Alert, Spin, Skeleton } from "antd";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import AppLayout from "../../../navigation/Layout/Layout";
import ScrollTabs from "../../components/ScrollTabs/ScrollTabs";
import { ROUTES } from "../../../routes/patientRoutes/routePaths";

import useAuthSession from "../../../authPatient/hooks/useAuthSession";
import { useUserInformation } from "../../hooks/useUserInformation";
import { useTour } from "../../../appointments/hooks/useTour";
import { profileTourSteps } from "./profileTourConfig";

import { fetchUserById } from "../../redux/thunks/userThunks";
import { fetchMedicalInfoByUserId } from "../../../Profile/redux/thunks/medicalThunks";
import { fetchBmiHistoryByUserId } from "../../../Profile/redux/thunks/bmiHistoryThunks";
import { fetchRelativesByUserId } from "../../../Profile/redux/thunks/relativesThunks";

// NEW: charger les historiques utilisés par la carte "Health History"
import { fetchPersonalHistoryByUserId } from "../../redux/thunks/personalHistoryThunks"; // NEW
import { fetchSurgicalHistory } from "../../redux/thunks/surgicalHistoryThunks"; // NEW
import { fetchFamilyHistory } from "../../redux/thunks/familyHistoryThunks"; // NEW

import ProfileCard from "../../components/ProfilePage/ProfileCard/ProfileCard";
import GeneralInfoCard from "../../components/ProfilePage/GeneralInfoCard/GeneralInfoCard";
import EditUserModal from "../../components/ProfilePage/EditUserModal/EditUserModal";

import PersonalHistory from "../../components/ProfilePage/PersonalHistory/PersonalHistory";
import EmergencyContact from "../../components/ProfilePage/Emergencycontact/EmergencyContact";

import MedicalQuestions from "../../components/ProfilePage/MedicalQuestions/MedicalQuestions";
import InsuranceCard from "../../components/ProfilePage/InsuranceCard/InsuranceCard";
import DoctorFollowersPanel from "../../../ProfileDoctor/componants/ProfileMedecinPage/DoctorFollowersPanel/DoctorFollowersPanel";


import "./Profile.css";

// ✅ i18n
import { useTranslation } from "react-i18next";

const { Content } = Layout;
const { Text } = Typography;

const normalizeInsurances = (value) => {
  if (Array.isArray(value)) return value;

  if (typeof value === "string" && value.trim()) {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [value];
    } catch {
      return [value];
    }
  }

  if (value && typeof value === "object") return [value];
  return [];
};

const DEV_SHOW_FAKE_RELATIVES =
  String(import.meta.env.VITE_SHOW_FAKE_RELATIVES).toLowerCase() === "true";
const PROFILE_TOUR_ENABLED = false;

function getActiveKey(pathname, userId) {
  const p = pathname.toLowerCase();
  if (p.startsWith(ROUTES.OVERVIEWS.toLowerCase())) return ROUTES.OVERVIEWS;
  if (p.startsWith(ROUTES.MEDICAL_RECORD.toLowerCase())) return ROUTES.MEDICAL_RECORD;
  if (p.startsWith("/profile")) return ROUTES.PROFILE(userId || ":userId");
  return ROUTES.OVERVIEWS;
}

function ProfileContent({
  user,
  medical,
  relatives,
  relativesLoading,
  relativesError,
  isBootLoading,
  userId,
  activeKey,
  onTabChange,
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [shouldStartTour, setShouldStartTour] = useState(false);

  const {
    isModalOpen,
    openModal,
    closeModal,
    formatDate,
    avatarSrc,
    handleSubmit,
  } = useUserInformation(user, medical);

  const dispatch = useDispatch();

  const relationshipLabel = (type) => {
    if (!type) return t("profil.relationship.relative");

    const key = String(type).toLowerCase();
    const map = {
      father: "profil.relationship.father",
      mother: "profil.relationship.mother",
      son: "profil.relationship.son",
      daughter: "profil.relationship.daughter",
      spouse: "profil.relationship.spouse",
      brother: "profil.relationship.brother",
      sister: "profil.relationship.sister",
      other: "profil.relationship.other",
      bro: "profil.relationship.brother",
      sis: "profil.relationship.sister",
    };

    const i18nKey = map[key];
    if (i18nKey) return t(i18nKey);

    return type.charAt(0).toUpperCase() + type.slice(1);
  };

  const members = useMemo(
    () =>
      (relatives ?? []).map((r) => ({
        id: r._id || r.id,
        relationship: relationshipLabel(r.relationshipType),
        name: r.relativeName || r.relativeEmail || r.relativeId || t("profil.family.linkedAccount"),
        avatar: r.avatarUrl,
        status: r.status,
      })),
    [relatives, t]
  );

 
 
 


  const profileInsurances = useMemo(
    () => normalizeInsurances(medical?.insurance),
    [medical?.insurance]
  );
  // Guide
  useEffect(() => {
    if (!PROFILE_TOUR_ENABLED) {
      setShouldStartTour(false);
      return;
    }

    const hasGuide = searchParams.get("guide") === "true";
    const hasTour = searchParams.get("tour") === "profile";
    let fallbackTimer;

    if (hasGuide || hasTour) {
      const startTimer = setTimeout(() => {
        const profileGrid = document.querySelector(".profile__grid");
        const profileCard = document.querySelector(".profileCard");
        const container = document.querySelector(".profile");

        if (profileGrid && profileCard && container) {
          setShouldStartTour(true);
        } else {
          fallbackTimer = setTimeout(() => setShouldStartTour(true), 1500);
        }
      }, 1000);

      return () => {
        clearTimeout(startTimer);
        clearTimeout(fallbackTimer);
      };
    }

    setShouldStartTour(false);
  }, [searchParams]);

  useTour(PROFILE_TOUR_ENABLED && shouldStartTour, profileTourSteps);

  return (
    <AppLayout>
      <div className="profile">
        <ScrollTabs activeKey={activeKey} onChange={onTabChange} userId={userId} />

        <div className="pr-content">
          <div className="profile-grid">
          
          
          
          
          
          
          
          

            <div className="profile__grid">
              {/* Left Column */}
              <div className="profile__col profile__col--left">
                <div className="profileCard">
                  {isBootLoading ? (
                    <Skeleton active paragraph={{ rows: 6 }} />
                  ) : (
                    <ProfileCard
                      user={user}
                      avatarSrc={avatarSrc}
                      onAvatarChange={() => dispatch(fetchUserById(userId))}
                    />
                  )}
                </div>

                <div className="profile__generalCard">
                  {isBootLoading ? (
                    <Skeleton active paragraph={{ rows: 8 }} />
                  ) : (
                    <GeneralInfoCard
                      user={user}
                      medical={medical || null}
                      formatDate={formatDate}
                      onEdit={openModal}
                    />
                  )}
                </div>

                <div className="profile__InsuranceCard">
                  {isBootLoading ? (
                    <Skeleton active paragraph={{ rows: 3 }} />
                  ) : (
                    <InsuranceCard
                      insurances={profileInsurances}
                      provider={medical?.insurance?.provider || null}
                      policyNo={medical?.insurance?.policyNumber || null}
                    />
                  )}
                </div>

                <div className="profile__EmergencyCard">
                  {isBootLoading ? (
                    <Skeleton active paragraph={{ rows: 2 }} />
                  ) : (
                    <EmergencyContact />
                  )}
                </div>
              </div>

              {/* Middle Column */}
              <div className="profile__col">
                <div className="card profile__healthHistory ph-card">
                  {isBootLoading ? (
                    <Skeleton active paragraph={{ rows: 6 }} />
                  ) : (
                    <PersonalHistory />
                  )}
                </div>

                <div className="card mq">
                  {isBootLoading ? (
                    <Skeleton active paragraph={{ rows: 5 }} />
                  ) : (
                    <MedicalQuestions />
                  )}
                </div>

      
            
            
            
            
            
              </div>

            </div>
          </div>

          <EditUserModal
            open={isModalOpen}
            onClose={closeModal}
            user={user}
            medical={medical || null}
            onSubmit={handleSubmit}
          />
        </div>
      </div>
    </AppLayout>
  );
}

export default function Profile() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const lastFetchedUserId = useRef(null);

  const { effectiveUserId, effectiveUser, isSubprofileContext } = useAuthSession({ auto: true });
  const userId = effectiveUserId;

  const userState = useSelector((s) => s.user);

  const userLoading = userState.loading;
  const userError = userState.error;

  // ✅ fetchUserById stores in users[userId], fetchUsers stores in usersById[userId]
  const users = useSelector((s) => s.user.users);
  const usersById = useSelector((s) => s.user.usersById);

  const user = useMemo(
    () => {
      if (!userId) return null;
      if (isSubprofileContext && effectiveUser) return effectiveUser;
      // ✅ Check both users (from fetchUserById) and usersById (from fetchUsers)
      return users?.[userId] ?? usersById?.[userId] ?? null;
    },
    [effectiveUser, isSubprofileContext, users, usersById, userId]
  );

  const medicalState = useSelector((s) => s.medical);
  const medicalHasFetched = userId ? !!medicalState?.fetchedUserIds?.[userId] : false;
  const medical = userId
    ? medicalState?.byId?.[userId] ?? (medicalHasFetched ? medicalState?.data : null)
    : null;
  
  const {
    data: relatives,
    isLoading: relativesLoading,
    errorMessage: relativesError,
  } = useSelector((s) => s.relatives);

  // ✅ Loading only if we haven't fetched yet
  const isBootLoading = (userLoading && !user) || (!medicalHasFetched && !medical);

  useEffect(() => {
    if (!userId || lastFetchedUserId.current === userId) return;
    lastFetchedUserId.current = userId;

    dispatch(fetchUserById(userId));
    dispatch(fetchMedicalInfoByUserId(userId));
    dispatch(fetchBmiHistoryByUserId(userId));
    dispatch(fetchRelativesByUserId(userId));

    // NEW: charger les données utilisées par la carte "Health History"
    dispatch(fetchPersonalHistoryByUserId(userId)); // NEW
    dispatch(fetchSurgicalHistory(userId)); // NEW
    dispatch(fetchFamilyHistory(userId)); // NEW
  }, [userId, dispatch]);

  const { pathname } = useLocation();
  const navigate = useNavigate();
  const activeKey = getActiveKey(pathname, userId);
  const onTabChange = (key) => navigate(key);

  if (!userId) {
    return (
      <AppLayout>
        <Content className="profile__center">
          <Alert
            message={t("profil.errors.unauthorizedTitle")}
            description={t("profil.errors.unauthorizedDesc")}
            type="warning"
            showIcon
          />
        </Content>
      </AppLayout>
    );
  }

  if (isBootLoading && !user) {
    return (
      <AppLayout>
        <Content className="profile__center">
          <Spin size="large" />
          <Text type="secondary" style={{ marginTop: 8, display: "block" }}>
            {t("profil.loading.profile")}
          </Text>
        </Content>
      </AppLayout>
    );
  }

  if (!isSubprofileContext && userError && !user) {
    return (
      <AppLayout>
        <Content className="profile__center">
          <Alert
            message={t("profil.errors.loadUserTitle")}
            description={userError}
            type="error"
            showIcon
          />
        </Content>
      </AppLayout>
    );
  }

  if (!user) {
    return (
      <AppLayout>
        <Content className="profile__center">
          <Alert
            message={t("profil.errors.notFoundTitle")}
            description={t("profil.errors.notFoundDesc")}
            type="error"
            showIcon
          />
        </Content>
      </AppLayout>
    );
  }

  return (
    <ProfileContent
      user={user}
      medical={medical}
      relatives={relatives}
      relativesLoading={relativesLoading}
      relativesError={relativesError}
      isBootLoading={isBootLoading}
      userId={userId}
      activeKey={activeKey}
      onTabChange={onTabChange}
    />
  );
}
